import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export const SelectionCard = ({
  selected = false,
  onClick,
  icon: Icon,
  image,
  title,
  subtitle,
  meta,
  hint,
  className,
  testId,
  index = 0,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{ animationDelay: `${index * 60}ms` }}
      className={cn(
        "selection-card group relative flex flex-col text-left w-full h-full overflow-hidden",
        "transition-[transform,background,border,box-shadow] duration-500 ease-out motion-reduce:duration-0",
        "active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-brand/40",
        "border rounded-2xl",
        image
          ? "items-stretch p-0 gap-0"
          : "items-start gap-4 p-5 md:p-6",
        image
          ? selected
            ? "border-brand-secondary [box-shadow:0_8px_40px_rgb(var(--brand-secondary)/0.25)] -translate-y-0.5 dark:border-brand-secondary/70 dark:[box-shadow:0_8px_40px_rgb(var(--brand-secondary)/0.2)]"
            : "border-zinc-200 hover:border-zinc-300 hover:-translate-y-1 hover:shadow-xl dark:border-white/[0.06] dark:hover:border-white/15 dark:hover:shadow-2xl"
          : selected
            ? "bg-zinc-900 border-brand-secondary text-zinc-50 shadow-[0_8px_40px_rgba(0,0,0,0.12)] dark:bg-zinc-800/90 dark:border-white/30 dark:shadow-[0_8px_40px_rgba(255,255,255,0.06)] -translate-y-0.5"
            : "bg-white/80 border-zinc-200 text-zinc-700 hover:bg-white hover:border-zinc-300 hover:-translate-y-1 hover:shadow-xl dark:bg-zinc-900/40 dark:border-white/[0.06] dark:text-zinc-300 dark:hover:bg-zinc-900/80 dark:hover:border-white/15 dark:hover:shadow-2xl",
        className
      )}
    >
      {image && (
        <div className="relative w-full aspect-[4/3] overflow-hidden">
          <img
            src={image}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-105 group-hover:brightness-110 motion-reduce:transition-none motion-reduce:group-hover:scale-100 motion-reduce:group-hover:brightness-100"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 flex flex-col gap-1.5 pb-4 md:pb-5">
            <h3 className="text-base md:text-lg lg:text-xl font-semibold tracking-tight leading-tight font-display text-white [text-shadow:0_1px_4px_rgba(0,0,0,0.7)]">
              {title}
            </h3>
            {subtitle && (
              <p className="text-sm font-medium text-white/80">
                {subtitle}
              </p>
            )}
            {hint && (
              <p className="text-xs leading-snug text-white/60">
                {hint}
              </p>
            )}
          </div>
        </div>
      )}

      <span
        className={cn(
          "absolute top-3 right-3 h-6 w-6 rounded-full flex items-center justify-center border transition-all duration-300 z-10",
          selected
            ? image
              ? "bg-brand-secondary border-brand-secondary text-white scale-100"
              : "bg-brand-secondary border-brand-secondary text-white scale-100 dark:bg-brand-secondary dark:border-brand-secondary dark:text-white"
            : image
              ? "bg-black/30 border-white/30 text-transparent scale-90 backdrop-blur-md dark:border-white/20"
              : "bg-transparent border-zinc-300 text-transparent scale-90 dark:border-white/15"
        )}
      >
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      </span>

      {!image && (
        <>
          {Icon && (
            <span
              className={cn(
                "h-11 w-11 rounded-xl flex items-center justify-center transition-colors flex-shrink-0",
                selected
                  ? "bg-zinc-50 text-zinc-900 dark:bg-zinc-50 dark:text-zinc-950"
                  : "bg-zinc-100 text-zinc-700 dark:bg-zinc-800/70 dark:text-zinc-300"
              )}
            >
              <Icon className="h-5 w-5" strokeWidth={1.75} />
            </span>
          )}

          <div className="flex flex-col gap-1">
            <h3
              className={cn(
                "text-base md:text-lg font-medium tracking-tight leading-tight font-display",
                selected
                  ? "text-zinc-50"
                  : "text-zinc-900 dark:text-zinc-50"
              )}
            >
              {title}
            </h3>
            {subtitle && (
              <p
                className={cn(
                  "text-sm font-medium",
                  selected ? "text-zinc-300" : "text-zinc-600 dark:text-zinc-400"
                )}
              >
                {subtitle}
              </p>
            )}
            {hint && (
              <p
                className={cn(
                  "text-xs mt-0.5 leading-snug",
                  selected ? "text-zinc-400" : "text-zinc-500 dark:text-zinc-500"
                )}
              >
                {hint}
              </p>
            )}
          </div>

          {meta && (
            <div
              className={cn(
                "mt-auto pt-2 text-xs tracking-[0.18em] uppercase font-semibold",
                selected ? "text-zinc-400" : "text-zinc-500 dark:text-zinc-500"
              )}
            >
              {meta}
            </div>
          )}
        </>
      )}
    </button>
  );
};
